import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from 'react';

export type CaseTab =
  | 'basic'
  | 'symptoms'
  | 'diagnoses'
  | 'treatments'
  | 'summary';

export interface NavigationState {
  currentCaseId?: string;
  activeTab: CaseTab;
}

export interface NavigationContextValue {
  navigationState: NavigationState;
  setCurrentCaseId: (id?: string) => void;
  setActiveTab: (tab: CaseTab) => void;
  navigateToCase: (id: string, tab?: CaseTab) => void;
  navigateToTab: (tab: CaseTab) => void;
  navigateToPath: (path: string) => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(
  undefined
);

export interface NavigationProviderProps {
  children: ReactNode;
  initialCaseId?: string;
  initialTab?: CaseTab;
  onNavigationChange?: (state: NavigationState) => void;
  onPathNavigation?: (path: string) => void;
}

/**
 * NavigationProvider - Provides navigation state management for case components.
 * This context abstracts navigation logic so components can work both with
 * React Router and as standalone components.
 */
export const NavigationProvider: React.FC<NavigationProviderProps> = ({
  children,
  initialCaseId,
  initialTab = 'basic',
  onNavigationChange,
  onPathNavigation,
}) => {
  const [navigationState, setNavigationState] = useState<NavigationState>({
    currentCaseId: initialCaseId,
    activeTab: initialTab,
  });

  const setCurrentCaseId = useCallback(
    (id?: string) => {
      setNavigationState((prev) => {
        const updatedState = { ...prev, currentCaseId: id };
        onNavigationChange?.(updatedState);
        return updatedState;
      });
    },
    [onNavigationChange]
  );

  const setActiveTab = useCallback(
    (tab: CaseTab) => {
      setNavigationState((prev) => {
        // Avoid unnecessary updates if the tab is already the same
        if (prev.activeTab === tab) {
          return prev;
        }
        const updatedState = { ...prev, activeTab: tab };
        onNavigationChange?.(updatedState);
        return updatedState;
      });
    },
    [onNavigationChange]
  );

  const navigateToCase = useCallback(
    (id: string, tab: CaseTab = 'basic') => {
      setNavigationState((prev) => {
        const updatedState = { ...prev, currentCaseId: id, activeTab: tab };
        onNavigationChange?.(updatedState);
        return updatedState;
      });
    },
    [onNavigationChange]
  );

  const navigateToTab = useCallback(
    (tab: CaseTab) => {
      setNavigationState((prev) => {
        // Avoid unnecessary updates if the tab is already the same
        if (prev.activeTab === tab) {
          return prev;
        }
        const updatedState = { ...prev, activeTab: tab };
        onNavigationChange?.(updatedState);
        return updatedState;
      });
    },
    [onNavigationChange]
  );

  const navigateToPath = useCallback(
    (path: string) => {
      onPathNavigation?.(path);
    },
    [onPathNavigation]
  );

  const value: NavigationContextValue = useMemo(
    () => ({
      navigationState,
      setCurrentCaseId,
      setActiveTab,
      navigateToCase,
      navigateToTab,
      navigateToPath,
    }),
    [
      navigationState,
      setCurrentCaseId,
      setActiveTab,
      navigateToCase,
      navigateToTab,
      navigateToPath,
    ]
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export const useNavigation = (): NavigationContextValue => {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};
